import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { Booking, CreateBookingDTO } from '@/types/booking.types';

export interface BookingsParams {
  page?: number;
  per_page?: number;
  status?: string;
  search?: string;
  start_date?: string;
  end_date?: string;
}

export interface BookingsResponse {
  data: Booking[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
  };
}

// Formato bruto da resposta da API Laravel
interface RawBookingsApiResponse {
  success: boolean;
  message: string;
  data: {
    bookings: {
      current_page: number;
      last_page: number;
      total: number;
      per_page: number;
      data: Booking[];
    };
  };
}

export const useBookingsQuery = (params?: BookingsParams) => {
  return useQuery({
    queryKey: ['bookings', params],
    queryFn: async (): Promise<BookingsResponse> => {
      const { data } = await api.get<RawBookingsApiResponse>('/v1/reservas', {
        params,
      });
      const bookings = data.data?.bookings;
      return {
        data: bookings?.data ?? [],
        meta: {
          current_page: bookings?.current_page ?? 1,
          last_page: bookings?.last_page ?? 1,
          total: bookings?.total ?? 0,
          per_page: bookings?.per_page ?? 15,
        },
      };
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

export const useBookingQuery = (id: string | number) => {
    return useQuery({
        queryKey: ['bookings', id],
        queryFn: async () => {
            const { data } = await api.get<{ success: boolean; message: string; data: { booking: Booking } }>(`/v1/reservas/${id}`);
            return data.data.booking;
        },
        enabled: !!id && String(id) !== 'new' && !isNaN(Number(id)),
    });
};

export interface MoveBookingPayload {
    new_room_id?: number | null;
    new_start_date?: string;
    new_end_date?: string;
}

export const useMoveBookingMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, ...payload }: MoveBookingPayload & { id: number }) => {
            const { data } = await api.patch<{ data: Booking }>(`/v1/reservas/${id}/mover`, payload);
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
        },
    });
};

export const useCreateBookingMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: CreateBookingDTO) => {
            const { data } = await api.post<{ success: boolean; message: string; data: { booking: Booking } }>('/v1/reservas', payload);
            return data.data.booking;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
        },
    });
};

export interface UpdateBookingPayload {
    arrival_date?: string;
    departure_date?: string;
    status?: string;
    notes?: string;
    main_guest_id?: number;
    room_id?: number;
    updated_at: string;
}

export const useUpdateBookingMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, ...payload }: UpdateBookingPayload & { id: number }) => {
            const { data } = await api.patch<{ success: boolean; message: string; data: { booking: Booking } }>(`/v1/reservas/${id}`, payload);
            return data.data.booking;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
            queryClient.invalidateQueries({ queryKey: ['bookings', variables.id] });
        },
    });
};

export const useCancelBookingMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, reason }: { id: number; reason?: string }) => {
            const { data } = await api.post<{ success: boolean; message: string; data: { booking: Booking } }>(`/v1/reservas/${id}/cancelar`, { reason });
            return data.data.booking;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
            queryClient.invalidateQueries({ queryKey: ['bookings', variables.id] });
        },
    });
};
